import { Component, ErrorInfo, ReactNode } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import RequireAuth from './components/auth/RequireAuth'
import RequireRol from './components/auth/RequireRol'
import RequireStaff from './components/auth/RequireStaff'
import { Alerta } from './components/kit'
import AppLayout from './components/layout/AppLayout'
import PublicLayout from './components/layout/PublicLayout'
import { SPA_BASE } from './config'
import Bien from './pages/app/Bien'
import Bienes from './pages/app/Bienes'
import AppInicio from './pages/app/Inicio'
import Integraciones from './pages/app/Integraciones'
import Oficina from './pages/app/Oficina'
import Oficinas from './pages/app/Oficinas'
import Perfil from './pages/app/Perfil'
import Planilla from './pages/app/Planilla'
import Bajas from './pages/app/Bajas'
import Plancheta from './pages/app/Plancheta'
import Teletrabajo from './pages/app/Teletrabajo'
import Traslados from './pages/app/Traslados'
import Usuarios from './pages/app/Usuarios'
import Login from './pages/Login'
import NoEncontrado from './pages/NoEncontrado'
import FichaBien from './pages/public/FichaBien'
import InicioPublico from './pages/public/Inicio'
import { RolApp } from './types/auth'
import { VEN_INVENTARIO } from './utils/roles'

/** Una página que solo ven las cuentas con alguno de los roles (la API igual lo exige). */
const conRol = (roles: RolApp[], pagina: ReactNode) => <RequireRol roles={roles}>{pagina}</RequireRol>
const ADMINISTRA: RolApp[] = ['administra_bienes']

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="pagina-centrada">
          <Alerta tipo="error" titulo="La página falló">Recarga la página. Si vuelve a pasar, avísanos qué estabas haciendo.</Alerta>
        </div>
      )
    }
    return this.props.children
  }
}

// El router vive bajo el prefijo del SPA ('/' en dev, '/administracion' en producción).
const basename = SPA_BASE === '/' ? '/' : SPA_BASE.replace(/\/$/, '')

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter basename={basename} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Parte pública: barra de aplicación del kit + pie plano */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<InicioPublico />} />
            <Route path="/login" element={<Login />} />
            {/* La ficha que abre el QR de la etiqueta: con sesión, sin rol y sin el resto de la app. */}
            <Route
              path="/b/:clave"
              element={
                <RequireAuth>
                  <FichaBien />
                </RequireAuth>
              }
            />
          </Route>

          {/* App autenticada: barra lateral del kit */}
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<AppInicio />} />
            <Route path="oficinas" element={conRol(VEN_INVENTARIO, <Oficinas />)} />
            <Route path="oficinas/:codigo" element={conRol(VEN_INVENTARIO, <Oficina />)} />
            <Route path="oficinas/:codigo/plancheta" element={conRol(ADMINISTRA, <Plancheta />)} />
            <Route path="bienes" element={conRol(VEN_INVENTARIO, <Bienes />)} />
            <Route path="bienes/planilla" element={conRol(ADMINISTRA, <Planilla />)} />
            <Route path="bienes/:id" element={conRol(VEN_INVENTARIO, <Bien />)} />
            <Route path="traslados" element={conRol(VEN_INVENTARIO, <Traslados />)} />
            <Route path="bajas" element={conRol(ADMINISTRA, <Bajas />)} />
            <Route path="teletrabajo" element={conRol(ADMINISTRA, <Teletrabajo />)} />
            <Route
              path="usuarios"
              element={
                <RequireStaff>
                  <Usuarios />
                </RequireStaff>
              }
            />
            <Route path="perfil" element={<Perfil />} />
            <Route
              path="integraciones"
              element={
                <RequireStaff>
                  <Integraciones />
                </RequireStaff>
              }
            />
            <Route path="*" element={<NoEncontrado />} />
          </Route>

          <Route path="*" element={<NoEncontrado />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
